import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  Paper,
  Button,
  alpha,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * ProjectTypeFilter - Material-UI filter component with pill-shaped filters
 * Displays project types as selectable chips
 */
const ProjectTypeFilter = ({ filters = [], onFilterChange, onClearAll }) => {
  const selectedCount = filters.filter(f => f.selected).length;

  const handleChipClick = (filterId) => {
    onFilterChange(filterId);
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        bgcolor: alpha('#ffffff', 0.5),
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            flex: { xs: '1 1 100%', sm: 'auto' },
          }}
        >
          Filter by Project Type
          {selectedCount > 0 && (
            <Typography
              component="span"
              sx={{
                ml: 1,
                px: 1.5,
                py: 0.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {selectedCount} selected
            </Typography>
          )}
        </Typography>

        {selectedCount > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={onClearAll}
            sx={{
              textTransform: 'capitalize',
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                bgcolor: alpha('#d32f2f', 0.08),
                borderColor: 'error.main',
              },
            }}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {filters.map((filter) => (
          <Chip
            key={filter.id}
            label={`${filter.label} (${filter.count})`}
            onClick={() => handleChipClick(filter.id)}
            onDelete={filter.selected ? () => handleChipClick(filter.id) : undefined}
            variant={filter.selected ? 'filled' : 'outlined'}
            color={filter.selected ? 'primary' : 'default'}
            icon={filter.selected ? undefined : undefined}
            sx={{
              cursor: 'pointer',
              fontWeight: filter.selected ? 600 : 500,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '20px',
              fontSize: '0.875rem',
              px: 1.5,
              py: 2.5,
              minHeight: '36px',
              '&:hover': {
                boxShadow: filter.selected ? 2 : 1,
                transform: 'translateY(-2px)',
              },
              ...(filter.selected && {
                boxShadow: `0 4px 12px ${alpha('#344a9d', 0.3)}`,
              }),
            }}
          />
        ))}
      </Stack>
    </Paper>
  );
};

export default ProjectTypeFilter;